import { IconMoreHorizontal } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { RailItem } from '../lib/rail';
import { signOut } from '../lib/sign-out';
import { Avatar } from './Avatar';
import { Popover } from './Popover';

// The sidebar's user block, and the account menu it opens (Din, 2026-10-20: the
// "…" was a bare icon that opened nothing). The WHOLE block is the trigger, as
// `OrgSwitcher`'s whole row is: the "…" stays inside as the affordance, and the
// target is the rail's width instead of a 1rem glyph, which is what a thumb hits
// in the phone drawer. Its name leads with the login it shows (WCAG 2.5.3).
//
// Mechanics are `<Popover/>`'s: outside click, Escape, focus back to the
// trigger, and a non-modal layer on `lib/dialog-trap.ts`'s stack, so inside the
// open drawer the first Escape closes this menu and the drawer keeps holding Tab.
//
// Rows: the `links` the caller hands in, then Sign out, a second door to the one
// sign-out (`lib/sign-out.ts`); the top bar keeps its own. No theme or language
// rows: the top bar and the drawer carry both.

export function UserMenu(props: {
  /** What the block shows: `/v1/me`'s `github_login`, then its email or role. */
  readonly login: string;
  readonly sub: string;
  readonly avatar: string | null;
  /**
   * The screens above Sign out: `USER_MENU` (`lib/rail.ts`) on the app host,
   * none on the admin host, whose URLs carry no org to land them in.
   */
  readonly links: readonly RailItem[];
  /** The shell's org-aware href resolver, so every row lands in the current org. */
  readonly resolve: (href: string) => string;
}): JSX.Element {
  return (
    <Popover
      class="app-sidebar__user"
      // The name carries both lines the trigger shows (WCAG 2.5.3, label in name).
      label={t('nav.user_menu', { login: props.login, sub: props.sub })}
      triggerClass="app-sidebar__user-trigger"
      panelClass="app-sidebar__user-menu"
      trigger={
        <>
          <Avatar class="app-sidebar__user-avatar" login={props.login} avatar={props.avatar} />
          {/* `{' '}` keeps the two lines two words in the text a checker reads off the
              trigger (adjacent spans' text joins with no separator); whitespace
              between block/flex children renders as nothing. */}
          <span class="app-sidebar__user-text">
            <span class="app-sidebar__user-name">{props.login}</span>{' '}
            <span class="app-sidebar__user-sub">{props.sub}</span>
          </span>
          <IconMoreHorizontal class="app-sidebar__user-more" />
        </>
      }
    >
      {(close) => (
        <ul>
          <For each={props.links}>
            {(item) => (
              <li>
                <A class="popover__item" href={props.resolve(item.href)} onClick={close}>
                  {t(item.label)}
                </A>
              </li>
            )}
          </For>
          <li>
            <button
              type="button"
              class="popover__item"
              onClick={() => {
                close();
                void signOut();
              }}
            >
              {t('session.sign_out')}
            </button>
          </li>
        </ul>
      )}
    </Popover>
  );
}
